/**
 * Review moderation.
 *
 * The screen is arranged around one distinction the old one could not make: a review that
 * somebody reported and that is STILL VISIBLE is a job, and a reported review that has
 * been hidden is finished work. Both were "Reported", so the queue never emptied and
 * nothing said how much of it was outstanding.
 *
 * The second thing it adds is the review nobody reported. A visible one-star review is
 * costing a vendor business whether or not a human flagged it, and no combination of the
 * old filters could find one — there was no rating filter and no search at all, so
 * answering "what did that customer complain about" meant paging through by eye.
 *
 * Hiding is the only action here, and it is not neutral: it recomputes the vendor's
 * average rating. So the row says what the rating is before you touch it.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Eye,
  EyeOff,
  Flag,
  MessageSquare,
  MoreHorizontal,
  Star,
  Store,
  ThumbsDown,
} from 'lucide-react';
import {
  enum_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import {
  FilterBar,
  SearchInput,
  SelectFilter,
  type ChipInput,
} from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { VendorFilter } from '@/components/filters/vendor-filter';
import { SavedViews } from '@/components/filters/saved-views';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/components/ui/toaster';
import { ago } from '@/lib/format';
import {
  use_hide_review,
  use_review_counts,
  use_review_list,
  use_unhide_review,
} from '@/features/reviews/hooks';
import {
  REVIEW_SORT_FIELDS,
  needs_action,
  rating_tone,
  stars,
  type AdminReview,
  type AdminReviewFilters,
  type ReviewCounts,
} from '@/features/reviews/types';

const SEGMENTS = ['reports', 'low', 'hidden', 'all'] as const;
type Segment = (typeof SEGMENTS)[number];

const SEGMENT_FILTERS: Record<Segment, Partial<AdminReviewFilters>> = {
  // Reported AND still on the vendor's page. The standing job.
  reports: { reported: true, hidden: false },
  // Nobody reported these; they are simply bad, and visible.
  low: { rating_max: 2, hidden: false },
  hidden: { hidden: true },
  all: {},
};

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  reports: {
    label: 'Reports',
    title: 'Reported and still showing',
    blurb: 'Somebody flagged these and they are still on the vendor’s page',
  },
  low: {
    label: 'Low ratings',
    title: 'One and two stars, still showing',
    blurb: 'Nobody reported these — they are just bad, and customers can see them',
  },
  hidden: {
    label: 'Hidden',
    title: 'Hidden reviews',
    blurb: 'Taken down and excluded from the vendor’s average',
  },
  all: { label: 'All', title: 'Reviews', blurb: 'Every review customers have left' },
};

export function ReviewListPage() {
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'reports';
  const copy = SEGMENT_COPY[segment];

  const [q, set_q] = use_url_state('q', string_param);
  const [rating, set_rating] = use_url_state('rating', enum_param(['1', '2', '3', '4', '5'] as const));
  const [vendor_id, set_vendor_id] = use_url_state('vendor_profile_id', string_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [sort, set_sort] = use_url_state('sort', sort_param(REVIEW_SORT_FIELDS, null));

  const reset_page = () => set_page(1);

  const filters: AdminReviewFilters = useMemo(
    () => ({
      ...SEGMENT_FILTERS[segment],
      page,
      page_size,
      q: q || undefined,
      // A hand-picked rating beats the segment's ceiling.
      ...(rating ? { rating, rating_max: undefined } : {}),
      vendor_profile_id: vendor_id || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [segment, page, page_size, q, rating, vendor_id, from, to],
  );

  const { data, isLoading, isFetching, error, refetch } = use_review_list(filters, sort);
  const { data: counts } = use_review_counts();

  const hide_m = use_hide_review();
  const unhide_m = use_unhide_review();

  async function set_hidden(r: AdminReview, hidden: boolean) {
    try {
      await (hidden ? hide_m : unhide_m).mutateAsync(r.id);
      notify.success(hidden ? 'Review hidden' : 'Review restored', {
        description: hidden
          ? `Off ${r.vendor_business_name ?? 'the vendor'}’s page, and out of their average rating.`
          : `Back on ${r.vendor_business_name ?? 'the vendor'}’s page and counted again.`,
      });
    } catch (err) {
      notify.error(err, 'Could not change this review.');
    }
  }

  /* --------------------------------- chips --------------------------------- */

  const chips: ChipInput[] = [
    q && { key: 'q', label: 'Search', value: q, on_clear: () => set_q('') },
    rating && {
      key: 'rating',
      label: 'Rating',
      value: `${rating} star${rating === '1' ? '' : 's'}`,
      on_clear: () => set_rating(''),
    },
    vendor_id && {
      key: 'vendor',
      label: 'Vendor',
      value: 'One vendor',
      on_clear: () => set_vendor_id(''),
    },
    (from || to) && {
      key: 'date',
      label: 'Posted',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
      },
    },
  ];

  function clear_all() {
    set_q('');
    set_rating('');
    set_vendor_id('');
    set_from('');
    set_to('');
    reset_page();
  }

  const metrics: Metric[] = useMemo(() => build_metrics(counts), [counts]);

  /* -------------------------------- columns -------------------------------- */

  const columns = useMemo<ColumnDef<AdminReview>[]>(
    () => [
      {
        id: 'rating',
        header: 'Rating',
        meta: { sort_key: 'rating', label: 'Rating', width: '8rem', csv: (r) => r.rating },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className="flex min-w-0 flex-col">
              <span
                className={
                  'whitespace-nowrap text-sm ' +
                  (rating_tone(r.rating) === 'destructive'
                    ? 'text-destructive'
                    : rating_tone(r.rating) === 'warning'
                      ? 'text-warning'
                      : 'text-success')
                }
                title={`${r.rating} out of 5`}
                data-testid={`rating-${r.id}`}
              >
                {stars(r.rating)}
              </span>
              <span className="whitespace-nowrap text-micro text-muted-foreground">
                {ago(r.created_at)}
              </span>
            </span>
          );
        },
      },
      {
        id: 'comment',
        header: 'What they said',
        enableSorting: false,
        meta: { label: 'Comment', width: '22rem', csv: (r) => r.comment ?? '' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className="flex min-w-0 items-start gap-2">
              <PersonAvatar path={null} name={r.reviewer_name} seed={r.id} size="sm" />
              <span className="flex min-w-0 flex-col">
                {r.comment ? (
                  <span className="line-clamp-2 text-data" title={r.comment}>
                    {r.comment}
                  </span>
                ) : (
                  <span className="text-data text-muted-foreground/60">
                    A rating with no words
                  </span>
                )}
                <span className="truncate text-micro text-muted-foreground">
                  {r.reviewer_name}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: 'vendor',
        header: 'About',
        enableSorting: false,
        meta: { label: 'Vendor', csv: (r) => r.vendor_business_name ?? '' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <Link
              to={`/vendors/${r.vendor_profile_id}`}
              onClick={(e) => e.stopPropagation()}
              className="truncate font-medium underline-offset-2 hover:text-primary hover:underline"
            >
              {r.vendor_business_name ?? 'Vendor removed'}
            </Link>
          );
        },
      },
      {
        id: 'reported',
        header: 'Reported',
        meta: {
          sort_key: 'reported_at',
          label: 'Reported',
          width: '14rem',
          csv: (r) => r.report_reason ?? '',
        },
        cell: ({ row }) => {
          const r = row.original;
          if (!r.reported_at) {
            return <span className="text-micro text-muted-foreground/60">Nobody flagged it</span>;
          }
          return (
            <span className="flex min-w-0 flex-col gap-0.5">
              <Badge tone="warning" size="sm" dot data-testid={`reported-${r.id}`}>
                <Flag className="h-3 w-3" /> {ago(r.reported_at)}
              </Badge>
              {r.report_reason && (
                <span className="line-clamp-1 text-micro text-muted-foreground" title={r.report_reason}>
                  {r.report_reason}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        meta: {
          label: 'Status',
          csv: (r) => (r.is_hidden ? 'Hidden' : needs_action(r) ? 'Needs a decision' : 'Showing'),
        },
        cell: ({ row }) => {
          const r = row.original;
          /*
           * Three states, not two. "Reported" alone never emptied: a complaint that has
           * been dealt with and one nobody has looked at read identically.
           */
          if (r.is_hidden) {
            return (
              <Badge tone="neutral" size="sm" dot data-testid={`status-${r.id}`}>
                Hidden
              </Badge>
            );
          }
          return needs_action(r) ? (
            <Badge tone="destructive" size="sm" dot data-testid={`status-${r.id}`}>
              Needs a decision
            </Badge>
          ) : (
            <Badge tone="success" size="sm" dot data-testid={`status-${r.id}`}>
              Showing
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { align: 'right', width: '1%' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Review actions"
                  data-testid={`row-actions-${r.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <RowAction
                  on_select={() => void set_hidden(r, !r.is_hidden)}
                  testid={`toggle-${r.id}`}
                >
                  {r.is_hidden ? (
                    <>
                      <Eye className="h-4 w-4" /> Put it back
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-4 w-4" /> Hide it
                    </>
                  )}
                </RowAction>
                <DropdownMenuSeparator />
                <RowAction
                  on_select={() => window.open(`/vendors/${r.vendor_profile_id}`, '_self')}
                  testid={`open-vendor-${r.id}`}
                >
                  <Store className="h-4 w-4" /> Open the vendor
                </RowAction>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      actions={<SavedViews module={`reviews:${segment}`} is_filtered={chips.some(Boolean)} />}
      tabs={
        <PageTabs
          current={segment}
          on_change={(v) => {
            set_segment(v as Segment);
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({
            value: v,
            label: SEGMENT_COPY[v].label,
            count:
              v === 'reports'
                ? counts?.unactioned
                : v === 'low'
                  ? counts?.low_rated
                  : v === 'hidden'
                    ? counts?.hidden
                    : undefined,
          }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<AdminReview>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(r) => r.id}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        storage_key={`reviews:${segment}`}
        export_name="reviews"
        caption="Customer reviews"
        test_id="review-list"
        empty={{
          icon: MessageSquare,
          title: chips.some(Boolean)
            ? 'No reviews match these filters'
            : segment === 'reports'
              ? 'No reports waiting'
              : segment === 'low'
                ? 'No low ratings on show'
                : `No ${copy.label.toLowerCase()} reviews`,
          description:
            segment === 'reports'
              ? 'Every report has been dealt with — nothing flagged is still on a vendor’s page.'
              : segment === 'low'
                ? 'Nothing at one or two stars is visible to customers right now.'
                : 'Reviews appear here as customers leave them.',
        }}
        filters={
          <FilterBar chips={chips} on_clear_all={clear_all}>
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Search"
              placeholder="Comment, reviewer or vendor…"
              test_id="filter-q"
            />
            <SelectFilter
              label="Rating"
              value={rating}
              on_change={(v) => {
                set_rating(v);
                reset_page();
              }}
              options={[
                { value: '1', label: '1 star' },
                { value: '2', label: '2 stars' },
                { value: '3', label: '3 stars' },
                { value: '4', label: '4 stars' },
                { value: '5', label: '5 stars' },
              ]}
              all_label="Any rating"
              test_id="filter-rating"
            />
            <VendorFilter
              vendor_id={vendor_id}
              on_change={(id) => {
                set_vendor_id(id);
                reset_page();
              }}
            />
            <DateRangeFilter
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
              label="Posted"
            />
          </FilterBar>
        }
      />
    </DashboardLayout>
  );
}

function build_metrics(counts: ReviewCounts | undefined): Metric[] {
  return [
    {
      key: 'unactioned',
      label: 'Reports waiting',
      /*
       * Reported AND still visible. The old screen counted every report ever raised, so
       * the number only ever went up and told nobody what was left to do.
       */
      value: counts?.unactioned?.toLocaleString('en-IN'),
      hint: 'Still on the vendor’s page',
      tone: counts && counts.unactioned > 0 ? 'destructive' : 'default',
      to: '/reviews?segment=reports',
      icon: Flag,
      loading: !counts,
    },
    {
      key: 'low_rated',
      label: 'One and two stars',
      value: counts?.low_rated?.toLocaleString('en-IN'),
      hint: 'Visible, nobody reported them',
      tone: counts && counts.low_rated > 0 ? 'warning' : 'default',
      to: '/reviews?segment=low',
      icon: ThumbsDown,
      loading: !counts,
    },
    {
      key: 'average',
      label: 'Average rating',
      value: counts?.average_rating ? counts.average_rating.toFixed(2) : '—',
      hint: 'Across everything visible',
      icon: Star,
      loading: !counts,
    },
    {
      key: 'hidden',
      label: 'Hidden',
      value: counts?.hidden?.toLocaleString('en-IN'),
      hint: 'Out of every vendor’s average',
      to: '/reviews?segment=hidden',
      icon: EyeOff,
      loading: !counts,
    },
    {
      key: 'total',
      label: 'Reviews',
      value: counts?.total?.toLocaleString('en-IN'),
      hint: counts ? `${counts.reported} reported ever` : undefined,
      to: '/reviews?segment=all',
      icon: MessageSquare,
      loading: !counts,
    },
  ];
}

/**
 * A dropdown item that survives Radix's pointer-based selection.
 *
 * Radix selects on pointerdown/pointerup rather than click, so a plain onClick
 * fires after the menu has begun closing and the row's own navigation wins.
 */
function RowAction({
  children,
  on_select,
  testid,
}: {
  children: React.ReactNode;
  on_select: () => void;
  testid: string;
}) {
  return (
    <DropdownMenuItem
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onSelect={on_select}
      data-testid={testid}
    >
      {children}
    </DropdownMenuItem>
  );
}
